import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import api from '../utils/api';
import confetti from 'canvas-confetti';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [pollingCount, setPollingCount] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const paymentStatus = searchParams.get('payment');

  useEffect(() => {
    if (paymentStatus === 'success' && sessionId) {
      // Fire confetti immediately
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      
      // Start polling for subscription activation
      checkSubscriptionStatus();
    } else if (paymentStatus === 'canceled') {
      setStatus('canceled');
    }
  }, [sessionId, paymentStatus]);

  const checkSubscriptionStatus = async () => {
    try {
      // Check if organization subscription is active
      const response = await api.get('/organization/me');
      const org = response.data;
      
      if (org.subscriptionStatus === 'active') {
        setStatus('success');
        setSubscriptionData({
          plan: org.subscriptionPlan,
          actions: org.actions_balance,
          monthlyActions: org.monthly_actions
        });
      } else if (pollingCount < 10) {
        // Poll again in 2 seconds (max 10 times = 20 seconds)
        setTimeout(() => {
          setPollingCount(prev => prev + 1);
          checkSubscriptionStatus();
        }, 2000);
      } else {
        // Timed out - webhook might be delayed
        setStatus('pending');
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      if (pollingCount < 10) {
        setTimeout(() => {
          setPollingCount(prev => prev + 1);
          checkSubscriptionStatus();
        }, 2000);
      } else {
        setStatus('error');
      }
    }
  };

  const handleContinue = () => {
    navigate('/admin');
    window.location.reload(); // Refresh to update subscription status
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-center">Processing Your Subscription...</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <Loader2 className="w-16 h-16 animate-spin text-blue-600 mx-auto" />
            <p className="text-gray-600">
              We're activating your account. This usually takes just a few seconds.
            </p>
            <div className="text-sm text-gray-500">
              Attempt {pollingCount + 1} of 10...
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-green-200 shadow-lg">
          <CardHeader className="text-center">
            <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-4" />
            <CardTitle className="text-3xl text-green-900">Welcome to BookingFlows!</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-900 mb-2">Your subscription is active! 🎉</h3>
              <div className="space-y-2 text-sm text-green-800">
                <div className="flex justify-between">
                  <span>Plan:</span>
                  <span className="font-semibold capitalize">{subscriptionData?.plan || 'Starter'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Monthly Actions:</span>
                  <span className="font-semibold">{subscriptionData?.monthlyActions || 100} actions</span>
                </div>
                <div className="flex justify-between">
                  <span>Current Balance:</span>
                  <span className="font-semibold">{subscriptionData?.actions || 100} actions</span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">What you get:</h4>
              <ul className="space-y-1 text-sm text-blue-800">
                <li>✅ AI-powered auto-scheduler</li>
                <li>✅ Automated payroll calculations</li>
                <li>✅ TeamMember & resources management</li>
                <li>✅ Customer booking widget</li>
                <li>✅ AI support assistant</li>
                <li>✅ Mobile apps (coming soon!)</li>
              </ul>
            </div>

            <Button 
              onClick={handleContinue}
              className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
            >
              Start Managing Your Resources →
            </Button>

            <p className="text-center text-sm text-gray-600">
              Need help? Chat with our AI assistant (costs 1 action) or email support.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-amber-200">
          <CardHeader>
            <CardTitle className="text-center text-amber-900">Payment Received!</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <CheckCircle className="w-16 h-16 text-amber-600 mx-auto" />
            <p className="text-gray-700">
              Your payment was successful! Your subscription is being activated.
            </p>
            <p className="text-sm text-gray-600">
              This can take up to a few minutes. You can start using BookingFlows now - 
              your subscription will be active shortly.
            </p>
            <Button onClick={handleContinue} className="w-full">
              Continue to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'canceled') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-center text-gray-900">Payment Canceled</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <XCircle className="w-16 h-16 text-gray-400 mx-auto" />
            <p className="text-gray-600">
              You canceled the payment. No charges were made.
            </p>
            <Button onClick={() => navigate('/admin')} variant="outline" className="w-full">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-200">
          <CardHeader>
            <CardTitle className="text-center text-red-900">Something Went Wrong</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <XCircle className="w-16 h-16 text-red-600 mx-auto" />
            <p className="text-gray-700">
              We couldn't verify your subscription status.
            </p>
            <p className="text-sm text-gray-600">
              Don't worry - if payment went through, your account will be activated automatically. 
              Try refreshing in a minute or contact support.
            </p>
            <div className="flex gap-2">
              <Button onClick={() => window.location.reload()} variant="outline" className="flex-1">
                Try Again
              </Button>
              <Button onClick={() => navigate('/admin')} className="flex-1">
                Go to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default SubscriptionSuccess;
